import {
  aiChatbotAnswerFeedbackClickedSrc4Evid1137,
  aiChatbotAnswerFeedbackFreeTextSrc4Evid1139,
  aiChatbotAnswerFeedbackPresentedSrc4Evid1136,
  aiChatbotAnswerFeedbackReasonButtonClickedSrc4Evid1138,
  aiChatbotResendButtonClickedSrc4Evid1173,
  breadcrumbsScroll,
  chatbotClickLink,
  chatbotClickOnSolution,
  chatbotConnectionLostMessagePresentedSrc4Evid1175,
  chatbotContactFlowQuickAccessAvailableSrc4Evid1134,
  chatbotConversationCreatedFromStartOver,
  chatbotDisplayWarning,
  chatbotErrorMessageAppearsSrc4Evid7401,
  chatbotErrorMessagePresentedSrc4Evid1172,
  chatbotGeneralClientLog,
  chatbotLoadChatbot,
  chatbotNotifications,
  chatbotSendMessageSrc4Evid7400,
  chatbotStartOver,
  chatbotStatusBannerStatusBannerClicked,
  chatbotStatusBannerStatusBannerDismissed,
  chatbotStatusBannerStatusBannerShowed,
  helpWidgetClickOnAnswerOrResultNewSrc4Evid1510,
  helpWidgetItemClickNewSrc4Evid1506,
  helpWidgetPageEngagementNewSrc4Evid1504,
  helpWidgetPageErrorNewSrc4Evid1507,
  helpWidgetPageViewNewSrc4Evid1502,
  helpWidgetScrolledToBottom,
  helpWidgetSearchResultsNewSrc4Evid1509,
  schedulerUserChoosesADay,
  schedulerUserClickedOnASlot,
  schedulerUserSearchedADifferentMonth,
  widgetContentInteraction,
  widgetEmptySearchResults,
  widgetPageEngagement,
  widgetPresentedContent,
  wixContactClickOnALinkInsideASolution,
  wixContactClickOnTheActionButton,
} from '@wix/bi-logger-customer-care-data/v2';
import type {
  aiChatbotAnswerFeedbackClickedSrc4Evid1137Params,
  aiChatbotAnswerFeedbackFreeTextSrc4Evid1139Params,
  aiChatbotAnswerFeedbackPresentedSrc4Evid1136Params,
  aiChatbotAnswerFeedbackReasonButtonClickedSrc4Evid1138Params,
  aiChatbotResendButtonClickedSrc4Evid1173Params,
  breadcrumbsScrollParams,
  chatbotClickLinkParams,
  chatbotClickOnSolutionParams,
  chatbotConnectionLostMessagePresentedSrc4Evid1175Params,
  chatbotContactFlowQuickAccessAvailableSrc4Evid1134Params,
  chatbotConversationCreatedFromStartOverParams,
  chatbotDisplayWarningParams,
  chatbotErrorMessageAppearsSrc4Evid7401Params,
  chatbotErrorMessagePresentedSrc4Evid1172Params,
  chatbotGeneralClientLogParams,
  chatbotLoadChatbotParams,
  chatbotNotificationsParams,
  chatbotSendMessageSrc4Evid7400Params,
  chatbotStartOverParams,
  chatbotStatusBannerStatusBannerClickedParams,
  chatbotStatusBannerStatusBannerDismissedParams,
  chatbotStatusBannerStatusBannerShowedParams,
  helpWidgetClickOnAnswerOrResultNewSrc4Evid1510Params,
  helpWidgetItemClickNewSrc4Evid1506Params,
  helpWidgetPageEngagementNewSrc4Evid1504Params,
  helpWidgetPageErrorNewSrc4Evid1507Params,
  helpWidgetPageViewNewSrc4Evid1502Params,
  helpWidgetScrolledToBottomParams,
  helpWidgetSearchResultsNewSrc4Evid1509Params,
  schedulerUserChoosesADayParams,
  schedulerUserClickedOnASlotParams,
  schedulerUserSearchedADifferentMonthParams,
  widgetContentInteractionParams,
  widgetEmptySearchResultsParams,
  widgetPageEngagementParams,
  widgetPresentedContentParams,
  wixContactClickOnALinkInsideASolutionParams,
  wixContactClickOnTheActionButtonParams,
} from '@wix/bi-logger-customer-care-data/v2/types';
import { factory } from '@wix/web-bi-logger';

import { TRootState } from '../common/reducers';
import { renderModel } from '../util/renderModel';
import { parentUrl, referral, referralUrl } from './constants';
import { RequiredWithUndefined } from './types';
import { createOnError, getSharedBiFieldsFromState } from './utils';

const accountId = renderModel.getAccountId();
const defaultBiFields = {
  bot_id: '717b0e2d-a564-4ef7-b965-481b1983133b',
  provider: 'answers',
  referral_url: referralUrl,
  parent_url: parentUrl,
  target_account_id: accountId,
  referral,
};

type BiParams<T> = Readonly<
  // __cookies_prefix__wixLanguage - tech solution related to migration to catalog v2. Do not need to send it for now.
  Omit<RequiredWithUndefined<T>, keyof typeof defaultBiFields | '__cookies_prefix__wixLanguage'>
>;

class BiLogger {
  private readonly _answerLogger = factory({ endpoint: 'ans_bi' }).updateDefaults(defaultBiFields).logger();
  private readonly _chatbotLogger = factory({ endpoint: 'chatbot' }).updateDefaults(defaultBiFields).logger();

  /**
   * Enhanced report method that automatically injects shared fields for new events
   * @param state - Redux state for extracting shared context fields
   * @param eventFunction - BI event function that takes params and returns event data
   * @param eventId - Event ID for error handling
   * @param params - Partial event parameters (shared fields will be auto-injected)
   */
  private async reportWithAutoEnhancement<T extends Record<string, any>>(
    state: TRootState,
    eventFunction: (params: T) => any,
    eventId: number,
    params: Partial<T>,
  ): Promise<void> {
    try {
      const sharedFields = await getSharedBiFieldsFromState(state);
      const enhancedParams = { ...params, ...sharedFields } as T;
      await this._chatbotLogger.report(eventFunction(enhancedParams));
    } catch (error) {
      createOnError(eventId, params)(error);
    }
  }

  // 574
  wixContactClickOnTheActionButton(params: BiParams<wixContactClickOnTheActionButtonParams>): void {
    this._answerLogger.report(wixContactClickOnTheActionButton(params)).catch(createOnError(574, params));
  }

  // 575
  wixContactClickOnALinkInsideASolution(params: BiParams<wixContactClickOnALinkInsideASolutionParams>): void {
    this._answerLogger
      .report(wixContactClickOnALinkInsideASolution(params))
      .catch(createOnError(575, params));
  }

  // 41
  chatbotConversationCreatedFromStartOver(
    params: BiParams<chatbotConversationCreatedFromStartOverParams>,
  ): void {
    this._chatbotLogger
      .report(chatbotConversationCreatedFromStartOver(params))
      .catch(createOnError(41, params));
  }

  // 1051
  chatbotLoadChatbot(params: BiParams<chatbotLoadChatbotParams>): void {
    this._chatbotLogger.report(chatbotLoadChatbot(params)).catch(createOnError(1051, params));
  }

  // 1054
  chatbotStartOver(params: BiParams<chatbotStartOverParams>): void {
    this._chatbotLogger.report(chatbotStartOver(params)).catch(createOnError(1054, params));
  }

  // 1088
  chatbotClickLink(params: BiParams<chatbotClickLinkParams>): void {
    this._chatbotLogger.report(chatbotClickLink(params)).catch(createOnError(1088, params));
  }

  // 1119
  widgetPresentedContent(params: BiParams<widgetPresentedContentParams>): void {
    this._chatbotLogger.report(widgetPresentedContent(params)).catch(createOnError(1119, params));
  }

  // 1120
  widgetContentInteraction(params: BiParams<widgetContentInteractionParams>): void {
    this._chatbotLogger.report(widgetContentInteraction(params)).catch(createOnError(1120, params));
  }

  // 1121
  widgetPageEngagement(params: BiParams<widgetPageEngagementParams>): void {
    this._chatbotLogger.report(widgetPageEngagement(params)).catch(createOnError(1121, params));
  }

  // 1123
  chatbotDisplayWarning(params: BiParams<chatbotDisplayWarningParams>): void {
    this._chatbotLogger.report(chatbotDisplayWarning(params)).catch(createOnError(1123, params));
  }

  // 1127
  chatbotStatusBannerStatusBannerShowed(params: BiParams<chatbotStatusBannerStatusBannerShowedParams>): void {
    this._chatbotLogger
      .report(chatbotStatusBannerStatusBannerShowed(params))
      .catch(createOnError(1127, params));
  }

  // 1128
  chatbotStatusBannerStatusBannerClicked(
    params: BiParams<chatbotStatusBannerStatusBannerClickedParams>,
  ): void {
    this._chatbotLogger
      .report(chatbotStatusBannerStatusBannerClicked(params))
      .catch(createOnError(1128, params));
  }

  // 1129
  chatbotStatusBannerStatusBannerDismissed(
    params: BiParams<chatbotStatusBannerStatusBannerDismissedParams>,
  ): void {
    this._chatbotLogger
      .report(chatbotStatusBannerStatusBannerDismissed(params))
      .catch(createOnError(1129, params));
  }

  // 1132
  widgetScrolledToBottom(params: BiParams<helpWidgetScrolledToBottomParams>): void {
    this._chatbotLogger.report(helpWidgetScrolledToBottom(params)).catch(createOnError(1132, params));
  }

  // 1151
  schedulerUserSearchedADifferentMonth(params: BiParams<schedulerUserSearchedADifferentMonthParams>): void {
    this._chatbotLogger
      .report(schedulerUserSearchedADifferentMonth(params))
      .catch(createOnError(1151, params));
  }

  // 1152
  schedulerUserChoosesADay(params: BiParams<schedulerUserChoosesADayParams>): void {
    this._chatbotLogger.report(schedulerUserChoosesADay(params)).catch(createOnError(1152, params));
  }

  // 1153
  schedulerUserClickedOnASlot(params: BiParams<schedulerUserClickedOnASlotParams>): void {
    this._chatbotLogger.report(schedulerUserClickedOnASlot(params)).catch(createOnError(1153, params));
  }

  // 1159
  chatbotClickOnSolution(params: BiParams<chatbotClickOnSolutionParams>): void {
    this._chatbotLogger.report(chatbotClickOnSolution(params)).catch(createOnError(1159, params));
  }

  // 1160
  widgetEmptySearchResults(params: BiParams<widgetEmptySearchResultsParams>): void {
    this._chatbotLogger.report(widgetEmptySearchResults(params)).catch(createOnError(1160, params));
  }

  // 1161
  breadcrumbsScroll(params: BiParams<breadcrumbsScrollParams>): void {
    this._chatbotLogger.report(breadcrumbsScroll(params)).catch(createOnError(1161, params));
  }

  // 1162
  chatbotNotifications(params: BiParams<chatbotNotificationsParams>): void {
    this._chatbotLogger.report(chatbotNotifications(params)).catch(createOnError(1162, params));
  }

  // 1172
  chatbotErrorMessagePresented(params: BiParams<chatbotErrorMessagePresentedSrc4Evid1172Params>): void {
    this._chatbotLogger
      .report(chatbotErrorMessagePresentedSrc4Evid1172(params))
      .catch(createOnError(1172, params));
  }

  // 1173
  resendButtonClicked(params: BiParams<aiChatbotResendButtonClickedSrc4Evid1173Params>): void {
    this._chatbotLogger
      .report(aiChatbotResendButtonClickedSrc4Evid1173(params))
      .catch(createOnError(1173, params));
  }

  // 1175
  chatbotConnectionLostMessagePresented(
    params: BiParams<chatbotConnectionLostMessagePresentedSrc4Evid1175Params>,
  ): void {
    this._chatbotLogger
      .report(chatbotConnectionLostMessagePresentedSrc4Evid1175(params))
      .catch(createOnError(1175, params));
  }

  // 7400
  chatbotSendMessage(params: BiParams<chatbotSendMessageSrc4Evid7400Params>): void {
    this._chatbotLogger.report(chatbotSendMessageSrc4Evid7400(params)).catch(createOnError(7400, params));
  }

  // 7401
  chatbotErrorMessageAppears(params: BiParams<chatbotErrorMessageAppearsSrc4Evid7401Params>): void {
    this._chatbotLogger
      .report(chatbotErrorMessageAppearsSrc4Evid7401(params))
      .catch(createOnError(7401, params));
  }

  chatbotGeneralClientLog(params: BiParams<chatbotGeneralClientLogParams>): void {
    this._chatbotLogger.report(chatbotGeneralClientLog(params)).catch(createOnError(1174, params));
  }

  // 1134
  chatbotContactFlowQuickAccessAvailable(
    params: BiParams<chatbotContactFlowQuickAccessAvailableSrc4Evid1134Params>,
  ): void {
    this._chatbotLogger
      .report(chatbotContactFlowQuickAccessAvailableSrc4Evid1134(params))
      .catch(createOnError(1134, params));
  }

  // 1136
  chatbotAnswerFeedbackPresented(params: BiParams<aiChatbotAnswerFeedbackPresentedSrc4Evid1136Params>): void {
    this._chatbotLogger
      .report(aiChatbotAnswerFeedbackPresentedSrc4Evid1136(params))
      .catch(createOnError(1136, params));
  }

  // 1137
  chatbotAnswerFeedbackClicked(params: BiParams<aiChatbotAnswerFeedbackClickedSrc4Evid1137Params>): void {
    this._chatbotLogger
      .report(aiChatbotAnswerFeedbackClickedSrc4Evid1137(params))
      .catch(createOnError(1137, params));
  }

  // 1138
  chatbotAnswerFeedbackReasonButtonClicked(
    params: BiParams<aiChatbotAnswerFeedbackReasonButtonClickedSrc4Evid1138Params>,
  ): void {
    this._chatbotLogger
      .report(aiChatbotAnswerFeedbackReasonButtonClickedSrc4Evid1138(params))
      .catch(createOnError(1138, params));
  }

  // 1139
  chatbotAnswerFeedbackFreeText(params: BiParams<aiChatbotAnswerFeedbackFreeTextSrc4Evid1139Params>): void {
    this._chatbotLogger
      .report(aiChatbotAnswerFeedbackFreeTextSrc4Evid1139(params))
      .catch(createOnError(1139, params));
  }

  // 1502
  async helpWidgetPageViewNew(
    state: TRootState,
    params: Partial<helpWidgetPageViewNewSrc4Evid1502Params>,
  ): Promise<void> {
    await this.reportWithAutoEnhancement(state, helpWidgetPageViewNewSrc4Evid1502, 1502, params);
  }

  // 1504
  async helpWidgetPageEngagementNew(
    state: TRootState,
    params: Partial<helpWidgetPageEngagementNewSrc4Evid1504Params>,
  ): Promise<void> {
    await this.reportWithAutoEnhancement(state, helpWidgetPageEngagementNewSrc4Evid1504, 1504, params);
  }

  // 1506
  async helpWidgetItemClickNew(
    state: TRootState,
    params: Partial<helpWidgetItemClickNewSrc4Evid1506Params>,
  ): Promise<void> {
    await this.reportWithAutoEnhancement(state, helpWidgetItemClickNewSrc4Evid1506, 1506, params);
  }

  // 1507
  async helpWidgetPageErrorNew(
    state: TRootState,
    params: Partial<helpWidgetPageErrorNewSrc4Evid1507Params>,
  ): Promise<void> {
    await this.reportWithAutoEnhancement(state, helpWidgetPageErrorNewSrc4Evid1507, 1507, params);
  }

  // 1509
  async helpWidgetSearchResultsNew(
    state: TRootState,
    params: Partial<helpWidgetSearchResultsNewSrc4Evid1509Params>,
  ): Promise<void> {
    await this.reportWithAutoEnhancement(state, helpWidgetSearchResultsNewSrc4Evid1509, 1509, params);
  }

  // 1510
  async helpWidgetClickOnAnswerOrResultNew(
    state: TRootState,
    params: Partial<helpWidgetClickOnAnswerOrResultNewSrc4Evid1510Params>,
  ): Promise<void> {
    await this.reportWithAutoEnhancement(state, helpWidgetClickOnAnswerOrResultNewSrc4Evid1510, 1510, params);
  }
}

export const biLogger = new BiLogger();
